import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useGallery } from '../context/GalleryContext';
import { Seo } from '../components/seo/Seo';
import { Brand } from '../components/brand/Brand';
import { SITE_NAME, SITE_URL, SITE_DEFAULT_DESCRIPTION, INSTAGRAM_LINK } from '../constants';

// Landing "Enter" gate. The hero is configurable (GALLERY_CONFIG.landing) and
// **orientation-responsive**: a portrait image (`artworkId`) on portrait devices, a landscape
// image (`landscapeArtworkId`) on landscape devices, via `<picture>` media queries. The
// `.hero-image` CSS fills the viewport (cover) and crops to a per-orientation focal point —
// `objectPosition` (portrait) / `landscapeObjectPosition` (landscape). The brand is left-aligned
// and shares the same left/right padding as the hero, so their content aligns.
export const LandingPage = () => {
    const { defaultRoute, landing, getArtwork } = useGallery();
    const portraitHero = getArtwork(landing.artworkId);
    const landscapeHero = landing.landscapeArtworkId ? getArtwork(landing.landscapeArtworkId) : undefined;
    const ogImage = portraitHero?.srcWebp ?? portraitHero?.src;

    const siteJsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, url: SITE_URL, name: SITE_NAME },
            {
                '@type': 'Person',
                '@id': `${SITE_URL}/#person`,
                name: SITE_NAME,
                jobTitle: 'Painter',
                url: SITE_URL,
                sameAs: [INSTAGRAM_LINK],
            },
        ],
    };

    return (
        <main className="flex min-h-[100dvh] flex-col bg-canvas p-8 md:p-16">
            <Seo title={SITE_NAME} description={SITE_DEFAULT_DESCRIPTION} image={ogImage} canonicalPath="/" jsonLd={siteJsonLd} />
            <h1 className="sr-only">Dragan Strukelj — Contemporary Painter</h1>
            <Helmet>
                {portraitHero && (
                    <link rel="preload" as="image" href={portraitHero.srcWebp ?? portraitHero.src} media="(orientation: portrait)" />
                )}
                {landscapeHero && (
                    <link rel="preload" as="image" href={landscapeHero.srcWebp ?? landscapeHero.src} media="(orientation: landscape)" />
                )}
            </Helmet>

            {/* Left-aligned; shares the same left/right padding as the hero below, so their content aligns.
                mb gives breathing room between the brand and the hero image. */}
            <div className="mb-10">
                <Brand />
            </div>

            <div className="relative flex-1 overflow-hidden">
                {portraitHero && (
                    <picture>
                        {landscapeHero?.srcWebp && (
                            <source media="(orientation: landscape)" srcSet={landscapeHero.srcWebp} type="image/webp" />
                        )}
                        {landscapeHero && <source media="(orientation: landscape)" srcSet={landscapeHero.src} />}
                        {portraitHero.srcWebp && <source srcSet={portraitHero.srcWebp} type="image/webp" />}
                        <img
                            src={portraitHero.src}
                            alt=""
                            loading="eager"
                            className="hero-image absolute inset-0 h-full w-full"
                            style={{
                                '--hero-pos-portrait': landing.objectPosition ?? 'center',
                                '--hero-pos-landscape': landing.landscapeObjectPosition ?? 'center top',
                            } as React.CSSProperties}
                            {...({ fetchpriority: 'high' } as any)}
                        />
                    </picture>
                )}
                <Link
                    to={`/${defaultRoute}`}
                    className="absolute inset-0 flex items-center justify-center opacity-60 motion-safe:transition-opacity motion-safe:duration-400 hover:opacity-100 focus-visible:opacity-100"
                >
                    <span className="text-enter font-light italic text-white [text-shadow:0_0_14px_#000]">Enter</span>
                </Link>
            </div>

            {/* Crawlable entry points for search engines. */}
            <nav aria-label="Site" className="sr-only">
                <Link to={`/${defaultRoute}`}>Enter the gallery</Link>
                <Link to="/about">About</Link>
                <Link to="/cv">CV</Link>
                <Link to="/contact">Contact</Link>
            </nav>
        </main>
    );
};
